import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMY, KIND_LABEL } from "@/lib/date";
import StatusBadge from "@/components/StatusBadge";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function CalendarView() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  useEffect(() => { api.get("/dashboard/calendar").then(r => setItems(r.data)); }, []);

  const byDay = useMemo(() => {
    const map = {};
    items.forEach(a => {
      if (!a.due_date) return;
      const key = String(a.due_date).slice(0, 10);
      (map[key] = map[key] || []).push(a);
    });
    return map;
  }, [items]);

  const first = new Date(cursor.y, cursor.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const shift = (dir) => setCursor(({ y, m }) => {
    const n = m + dir;
    if (n < 0) return { y: y - 1, m: 11 };
    if (n > 11) return { y: y + 1, m: 0 };
    return { y, m: n };
  });

  const selectedList = items
    .filter(a => a.due_date && a.due_date.startsWith(`${cursor.y}-${String(cursor.m+1).padStart(2,"0")}`))
    .sort((a,b) => (a.due_date || "").localeCompare(b.due_date || ""));

  return (
    <div className="p-10 max-w-[1400px]">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Timeline</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight">Deadline Calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm h-9" onClick={() => shift(-1)} data-testid="cal-prev"><CaretLeft size={14} weight="bold" /></Button>
          <div className="font-mono text-sm px-4">{MONTHS[cursor.m]} {cursor.y}</div>
          <Button variant="outline" className="rounded-sm h-9" onClick={() => shift(1)} data-testid="cal-next"><CaretRight size={14} weight="bold" /></Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="border border-border">
          <div className="grid grid-cols-7 border-b border-border">
            {WEEKDAYS.map(w => (
              <div key={w} className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-r last:border-r-0 border-border">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const key = d ? `${cursor.y}-${String(cursor.m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}` : null;
              const list = key ? (byDay[key] || []) : [];
              const isToday = key && key === new Date().toISOString().slice(0,10);
              return (
                <div key={i} className={`min-h-[92px] border-r border-b last:border-r-0 border-border p-2 ${!d ? "bg-[#F9F9F9]" : ""}`}>
                  {d && (
                    <>
                      <div className={`font-mono text-xs ${isToday ? "text-[#0F62FE] font-medium" : ""}`}>{d}</div>
                      <div className="mt-1 space-y-1">
                        {list.slice(0, 3).map((a, x) => (
                          <Link key={x} to={`/clients/${a.client_id}`}
                                className="block truncate text-[11px] font-mono border-l-2 pl-1"
                                style={{ borderColor: statusColor(a.status) }}>
                            {a.client_name}
                          </Link>
                        ))}
                        {list.length > 3 && <div className="text-[10px] text-muted-foreground font-mono">+{list.length - 3} more</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="border border-border">
          <header className="px-6 py-3 border-b border-border font-mono text-xs uppercase tracking-widest">
            {MONTHS[cursor.m]} deadlines · {selectedList.length}
          </header>
          <div className="max-h-[70vh] overflow-y-auto">
            {selectedList.length === 0 && <div className="p-6 text-sm text-muted-foreground">Nothing due this month.</div>}
            {selectedList.map((a, i) => (
              <Link key={i} to={`/clients/${a.client_id}`} className="block px-6 py-3 border-b border-border data-row">
                <div className="flex items-center justify-between">
                  <div className="font-medium truncate">{a.client_name}</div>
                  <StatusBadge status={a.status} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground font-mono">
                  {KIND_LABEL[a.kind]} · {fmtMY(a.due_date)}
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

function statusColor(s) {
  return s === "overdue" ? "#DA1E28" : s === "submitted" ? "#24A148" : s === "in_progress" ? "#0F62FE" : "#F1C21B";
}
